import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  financeConsoleCommandDraftV1Schema,
  financialReportRequestV1Schema,
  type CommandResultV1,
  type FinanceConsoleCommandDraftV1,
  type FinanceConsoleCommandTypeV1,
  type FinanceReferenceDataV1,
  type FinancialReportKindV1,
  type FinancialReportV1,
} from "@impetusos/contracts"

import {
  ConsoleRequestError,
  dispatchFinanceCommand,
  readFinanceReferenceData,
  readFinancialReport,
} from "../transport/index.js"
import { InteractionState } from "./InteractionState.js"

type FinanceArea =
  | "overview"
  | "chart"
  | "journals"
  | "receivables"
  | "payables"
  | "tax"
  | "periods"
  | "reports"
  | "reconciliation"
  | "cutover"

type LoadState = "loading" | "ready" | "denied" | "retry"

const areas = [
  ["overview", "Overview", "Close readiness and current financial posture"],
  ["chart", "Chart & setup", "Company, accounts, currency, and Branch dimensions"],
  ["journals", "Journals", "Balanced entries and explicit reversals"],
  ["receivables", "Receivables", "Invoices, payments, credits, and aging"],
  ["payables", "Payables", "Obligations, payments, credits, and aging"],
  ["tax", "Tax review", "Rule exceptions and reproducible decisions"],
  ["periods", "Periods", "Preview, close, late events, and controlled reopen"],
  ["reports", "Reports", "Authoritative statements and Branch profitability"],
  ["reconciliation", "Reconciliation", "Stripe and ERPNext convergence"],
  ["cutover", "Cutover", "Staged, balanced, approved opening state"],
] as const satisfies readonly [FinanceArea, string, string][]

const reportForArea: Record<FinanceArea, FinancialReportKindV1> = {
  overview: "profit_and_loss",
  chart: "balance_sheet",
  journals: "cash",
  receivables: "accounts_receivable_aging",
  payables: "accounts_payable_aging",
  tax: "tax_review",
  periods: "accounting_periods",
  reports: "branch_profitability",
  reconciliation: "reconciliation",
  cutover: "balance_sheet",
}

const reportLabels: Record<FinancialReportKindV1, string> = {
  profit_and_loss: "Profit & loss",
  balance_sheet: "Balance sheet",
  cash: "Cash activity",
  accounts_receivable_aging: "Receivables aging",
  accounts_payable_aging: "Payables aging",
  tax_review: "Tax review",
  branch_profitability: "Branch profitability",
  purchasing: "Purchasing",
  inventory_valuation: "Inventory valuation",
  inventory_exceptions: "Inventory exceptions",
  fleet_maintenance: "Fleet maintenance",
  reconciliation: "Reconciliation",
  accounting_periods: "Accounting periods",
}

const actionForArea: Partial<Record<FinanceArea, {
  label: string
  capability: string
}>> = {
  chart: { label: "Configure accounting", capability: "finance.manage" },
  journals: { label: "Post journal", capability: "finance.manage" },
  receivables: { label: "Create receivable", capability: "finance.manage" },
  payables: { label: "Create payable", capability: "finance.manage" },
  tax: { label: "Resolve review", capability: "finance.manage" },
  periods: { label: "Manage period", capability: "finance.period.close" },
  reconciliation: { label: "Run reconciliation", capability: "finance.reconcile" },
  cutover: { label: "Start cutover", capability: "finance.cutover.manage" },
}

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
})

const formatMinor = (value: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value / 100)
  } catch {
    return money.format(value / 100)
  }
}

const isoDate = (date: Date) => date.toISOString().slice(0, 10)
const today = isoDate(new Date())
const yearStart = `${new Date().getUTCFullYear()}-01-01`
const yearEnd = `${new Date().getUTCFullYear()}-12-31`

const field = (form: FormData, name: string): string => {
  const value = form.get(name)
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`Missing ${name}`)
  }
  return value.trim()
}

const amountMinor = (form: FormData): number => {
  const value = Number(field(form, "amount"))
  if (!Number.isFinite(value) || value <= 0) throw new Error("Invalid amount")
  return Math.round(value * 100)
}

const jsonRecord = (form: FormData, name: string): Record<string, string | number | boolean | null> => {
  const value: unknown = JSON.parse(field(form, name))
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Invalid record payload")
  }
  return value as Record<string, string | number | boolean | null>
}

const draft = (
  commandType: FinanceConsoleCommandTypeV1,
  entityType: string,
  entityId: string,
  legalEntityId: string,
  branchId: string | null,
  expectedVersion: number,
  payload: FinanceConsoleCommandDraftV1["payload"],
): FinanceConsoleCommandDraftV1 => {
  const operationId = crypto.randomUUID()
  return financeConsoleCommandDraftV1Schema.parse({
    requestVersion: 1,
    commandId: crypto.randomUUID(),
    correlationId: crypto.randomUUID(),
    idempotencyKey: `finance-console-${operationId}`,
    commandType,
    commandVersion: 1,
    expectedVersion,
    context: { legalEntityId, branchId, entityType, entityId },
    payload,
  })
}

const statusLabel = (status: string) => status.replaceAll("_", " ")

export function FinanceWorkspace({
  legalEntityId,
  legalEntityName,
  branchId,
  branchName,
  capabilities,
}: {
  legalEntityId: FinanceReferenceDataV1["legalEntityId"]
  legalEntityName: string
  branchId: FinanceReferenceDataV1["branchId"] | null
  branchName: string | null
  capabilities: readonly string[]
}) {
  const [area, setArea] = useState<FinanceArea>("overview")
  const [reportKind, setReportKind] = useState<FinancialReportKindV1>(
    reportForArea.overview,
  )
  const [report, setReport] = useState<FinancialReportV1 | null>(null)
  const [reference, setReference] = useState<FinanceReferenceDataV1 | null>(null)
  const [referenceState, setReferenceState] = useState<LoadState>("loading")
  const [loadState, setLoadState] = useState<LoadState>("loading")
  const [actionOpen, setActionOpen] = useState(false)
  const [actionPending, setActionPending] = useState(false)
  const [receipt, setReceipt] = useState<CommandResultV1 | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const actionDialogRef = useRef<HTMLDialogElement>(null)

  const capabilitiesSet = useMemo(() => new Set(capabilities), [capabilities])
  const branchScoped = !["accounting_periods", "reconciliation"].includes(reportKind)
  const selectedAction = actionForArea[area]
  const canAct = selectedAction !== undefined
    && capabilitiesSet.has(selectedAction.capability)
    && reference !== null
  const periodPreviewActive = area === "periods"
    && receipt?.ok === true
    && ["preview", "reopen_preview"].includes(String(receipt.result.state))
  const periodClosePreview = periodPreviewActive && receipt?.ok === true
    && receipt.result.state === "preview"
  const periodReopenPreview = periodPreviewActive && receipt?.ok === true
    && receipt.result.state === "reopen_preview"
  const periodCanClose = periodClosePreview && receipt.result.canClose === true
  const periodCanSubmit = !periodPreviewActive || periodReopenPreview || periodCanClose

  useEffect(() => {
    const dialog = actionDialogRef.current
    if (dialog === null) return
    if (actionOpen && !dialog.open) dialog.showModal()
    if (!actionOpen && dialog.open) dialog.close()
  }, [actionOpen])

  const loadReport = useCallback(async () => {
    setLoadState("loading")
    try {
      const next = await readFinancialReport(financialReportRequestV1Schema.parse({
        reportVersion: 1,
        kind: reportKind,
        legalEntityId,
        branchId: branchScoped ? branchId : null,
        fromDate: yearStart,
        toDate: today,
        cursor: 0,
        pageSize: 25,
      }))
      setReport(next)
      setLoadState("ready")
    } catch (error) {
      setReport(null)
      setLoadState(
        error instanceof ConsoleRequestError && error.kind === "denied"
          ? "denied"
          : "retry",
      )
    }
  }, [branchId, branchScoped, legalEntityId, reportKind])

  const loadReference = useCallback(async () => {
    if (branchId === null) {
      setReference(null)
      setReferenceState("denied")
      return
    }
    setReferenceState("loading")
    try {
      setReference(await readFinanceReferenceData({
        requestVersion: 1,
        legalEntityId,
        branchId,
      }))
      setReferenceState("ready")
    } catch {
      setReference(null)
      setReferenceState("retry")
    }
  }, [branchId, legalEntityId])

  useEffect(() => { void loadReport() }, [loadReport])
  useEffect(() => { void loadReference() }, [loadReference])

  const selectArea = (next: FinanceArea) => {
    setArea(next)
    setReportKind(reportForArea[next])
    setActionOpen(false)
    setReceipt(null)
    setNotice(null)
  }

  const submit = async (command: FinanceConsoleCommandDraftV1) => {
    setActionPending(true)
    setNotice(null)
    try {
      const result = await dispatchFinanceCommand(command)
      setReceipt(result)
      if (result.ok) {
        setNotice("Frappe confirmed the command. The authoritative view has been refreshed.")
        if (!["finance.period.preview", "finance.period.reopen_preview"].includes(
          command.commandType,
        )) setActionOpen(false)
        await Promise.all([loadReport(), loadReference()])
      } else {
        setNotice(result.error.message)
      }
    } catch (error) {
      setNotice(
        error instanceof ConsoleRequestError && error.kind === "denied"
          ? "Your current Membership does not authorize this action."
          : "The command was not confirmed. No success has been assumed.",
      )
    } finally {
      setActionPending(false)
    }
  }

  const submitAction = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (branchId === null) {
      setNotice("Select a Branch before submitting this workflow.")
      return
    }
    const form = new FormData(event.currentTarget)
    try {
      if (area === "chart") {
        const configurationId = field(form, "configurationId")
        void submit(draft(
          "finance.configuration.configure",
          "finance_configuration",
          configurationId,
          legalEntityId,
          branchId,
          Number(field(form, "expectedVersion")),
          {
            revisionId: crypto.randomUUID(),
            company: field(form, "company"),
            fiscalYear: field(form, "fiscalYear"),
            fiscalYearStart: field(form, "fiscalYearStart"),
            fiscalYearEnd: field(form, "fiscalYearEnd"),
            currency: field(form, "currency").toUpperCase(),
            accounts: {
              receivableAccount: field(form, "receivableAccount"),
              payableAccount: field(form, "payableAccount"),
              incomeAccount: field(form, "incomeAccount"),
              expenseAccount: field(form, "expenseAccount"),
              roundOffAccount: field(form, "roundOffAccount"),
            },
            branches: [{
              branchId,
              costCenter: field(form, "costCenter"),
              accountingDimension: field(form, "accountingDimension"),
            }],
          },
        ))
      } else if (area === "journals") {
        const journalAction = field(form, "journalAction")
        const date = field(form, "postingDate")
        if (journalAction === "reverse") {
          const postingId = field(form, "postingId")
          void submit(draft(
            "finance.posting.reverse",
            "financial_posting",
            postingId,
            legalEntityId,
            branchId,
            Number(field(form, "expectedVersion")),
            {
              reversalId: crypto.randomUUID(),
              postingDate: date,
              reason: field(form, "reason"),
            },
          ))
          return
        }
        if (journalAction === "correction") {
          const correctionId = crypto.randomUUID()
          void submit(draft(
            "finance.correction.post",
            "financial_correction",
            correctionId,
            legalEntityId,
            branchId,
            0,
            {
              correctionId,
              legalEntityId,
              branchId,
              kind: "operator_correction",
              sourceType: "financial_posting",
              sourceId: field(form, "sourceId"),
              sourceVersion: Number(field(form, "sourceVersion")),
              billingProfileId: null,
              invoiceId: null,
              paymentAttemptId: null,
              providerEventId: null,
              currency: field(form, "sourceCurrency"),
              amountMinor: Number(field(form, "sourceAmountMinor")),
              originalEventAt: field(form, "originalEventAt"),
              postingDate: date,
              accountingMode: "link_existing",
              existingDocument: {
                documentType: "Journal Entry",
                documentId: field(form, "journalEntry"),
              },
              reason: field(form, "reason"),
              lines: [],
            },
          ))
          return
        }
        const amount = amountMinor(form)
        const costCenter = field(form, "costCenter")
        const postingId = crypto.randomUUID()
        const sourceId = journalAction === "late_adjustment"
          ? field(form, "sourceId")
          : crypto.randomUUID()
        const lines = [
          {
            lineId: crypto.randomUUID(),
            account: field(form, "debitAccount"),
            branchId,
            costCenter,
            debitMinor: amount,
            creditMinor: 0,
          },
          {
            lineId: crypto.randomUUID(),
            account: field(form, "creditAccount"),
            branchId,
            costCenter,
            debitMinor: 0,
            creditMinor: amount,
          },
        ]
        if (journalAction === "late_adjustment") {
          void submit(draft(
            "finance.late_adjustment.post",
            "financial_posting",
            postingId,
            legalEntityId,
            branchId,
            0,
            {
              adjustmentId: postingId,
              legalEntityId,
              branchId,
              sourceId,
              sourceVersion: Number(field(form, "sourceVersion")),
              configurationId: field(form, "configurationId"),
              configurationVersion: Number(field(form, "configurationVersion")),
              postingDate: date,
              reason: field(form, "reason"),
              lines,
            },
          ))
          return
        }
        void submit(draft(
          "finance.posting.post",
          "financial_posting",
          postingId,
          legalEntityId,
          branchId,
          0,
          {
            configurationId: field(form, "configurationId"),
            configurationVersion: Number(field(form, "configurationVersion")),
            currency: field(form, "currency").toUpperCase(),
            postingDate: date,
            originalEventAt: `${date}T12:00:00Z`,
            purpose: "adjustment",
            sourceType: "accountant_journal",
            sourceId,
            sourceVersion: 1,
            remarks: field(form, "reason"),
            lines,
          },
        ))
      } else if (area === "receivables" || area === "payables") {
        const isReceivable = area === "receivables"
        const action = field(form, "ledgerAction")
        const entityId = action === "invoice"
          ? crypto.randomUUID()
          : field(form, "recordId")
        const date = field(form, "postingDate")
        const common = {
          action,
          currency: field(form, "currency").toUpperCase(),
          postingDate: date,
          amountMinor: amountMinor(form),
        }
        let payload: FinanceConsoleCommandDraftV1["payload"]
        if (action === "invoice") {
          payload = isReceivable
            ? {
                ...common,
                customer: field(form, "party"),
                itemCode: field(form, "itemCode"),
                costCenter: field(form, "costCenter"),
                incomeAccount: field(form, "primaryAccount"),
                receivableAccount: field(form, "controlAccount"),
                dueDate: field(form, "dueDate"),
                description: field(form, "description"),
              }
            : {
                ...common,
                supplier: field(form, "party"),
                itemCode: field(form, "itemCode"),
                costCenter: field(form, "costCenter"),
                expenseAccount: field(form, "primaryAccount"),
                payableAccount: field(form, "controlAccount"),
                dueDate: field(form, "dueDate"),
                description: field(form, "description"),
              }
        } else if (action === "payment") {
          payload = {
            ...common,
            paymentId: crypto.randomUUID(),
            [isReceivable ? "paidToAccount" : "paidFromAccount"]: field(form, "settlementAccount"),
            referenceNumber: field(form, "referenceNumber"),
            referenceDate: field(form, "referenceDate"),
          }
        } else if (action === "credit") {
          payload = { ...common, creditId: crypto.randomUUID(), reason: field(form, "reason") }
        } else if (action === "write_off") {
          payload = {
            ...common,
            writeOffId: crypto.randomUUID(),
            [isReceivable ? "expenseAccount" : "writeOffAccount"]: field(form, "writeOffAccount"),
            reason: field(form, "reason"),
          }
        } else {
          payload = { ...common, correctionId: crypto.randomUUID(), reason: field(form, "reason") }
        }
        void submit(draft(
          isReceivable
            ? "finance.receivable.administer"
            : "finance.payable.administer",
          isReceivable ? "receivable" : "payable",
          entityId,
          legalEntityId,
          branchId,
          action === "invoice" ? 0 : Number(field(form, "expectedVersion")),
          payload,
        ))
      } else if (area === "tax") {
        const reviewId = field(form, "reviewId")
        const decision = field(form, "decision")
        void submit(draft(
          "transaction_tax.review.resolve",
          "transaction_tax_review",
          reviewId,
          legalEntityId,
          branchId,
          Number(field(form, "expectedVersion")),
          {
            decision,
            selectedRuleVersionId: decision === "approve" ? field(form, "ruleVersionId") : null,
            reason: field(form, "reason"),
          },
        ))
      } else if (area === "periods") {
        const periodOperation = field(form, "periodOperation")
        if (periodOperation === "reopen") {
          const preview = receipt?.ok && receipt.result.state === "reopen_preview"
            ? receipt.result
            : null
          const periodId = preview && typeof preview.entityId === "string"
            ? preview.entityId
            : field(form, "periodId")
          if (preview && typeof preview.previewFingerprint === "string") {
            void submit(draft(
              "finance.period.reopen",
              "accounting_period",
              periodId,
              legalEntityId,
              branchId,
              typeof preview.version === "number" ? preview.version : 1,
              {
                periodId,
                legalEntityId,
                previewFingerprint: preview.previewFingerprint,
                reason: field(form, "reason"),
                sandboxStepUpConfirmed: form.get("sandboxStepUpConfirmed") === "on",
              },
            ))
            return
          }
          void submit(draft(
            "finance.period.reopen_preview",
            "accounting_period",
            periodId,
            legalEntityId,
            branchId,
            1,
            { periodId, legalEntityId },
          ))
          return
        }
        const preview = receipt?.ok && receipt.result.state === "preview"
          ? receipt.result
          : null
        const periodId = preview && typeof preview.entityId === "string"
          ? preview.entityId
          : field(form, "periodId")
        const closeReady = preview
          && preview.canClose === true
          && typeof preview.previewFingerprint === "string"
          && typeof preview.startDate === "string"
          && typeof preview.endDate === "string"
        if (closeReady) {
          void submit(draft(
            "finance.period.close",
            "accounting_period",
            periodId,
            legalEntityId,
            branchId,
            typeof preview.version === "number" ? preview.version : 0,
            {
              periodId,
              legalEntityId,
              startDate: String(preview.startDate),
              endDate: String(preview.endDate),
              previewFingerprint: String(preview.previewFingerprint),
              reason: field(form, "reason"),
            },
          ))
          return
        }
        void submit(draft(
          "finance.period.preview",
          "accounting_period",
          periodId,
          legalEntityId,
          branchId,
          0,
          {
            periodId,
            legalEntityId,
            startDate: field(form, "startDate"),
            endDate: field(form, "endDate"),
          },
        ))
      } else if (area === "reconciliation") {
        const reconciliationId = crypto.randomUUID()
        const createdFrom = `${field(form, "createdFrom")}T00:00:00Z`
        const createdTo = `${field(form, "createdTo")}T23:59:59Z`
        void submit(draft(
          "stripe.accounting.reconcile",
          "stripe_accounting_reconciliation",
          reconciliationId,
          legalEntityId,
          branchId,
          0,
          {
            reconciliationId,
            legalEntityId,
            branchId,
            createdFrom,
            createdTo,
            observedAt: new Date().toISOString(),
            limit: 100,
            startingAfter: null,
          },
        ))
      } else if (area === "cutover") {
        const cutoverAction = field(form, "cutoverAction")
        const batchId = cutoverAction === "create"
          ? crypto.randomUUID()
          : field(form, "batchId")
        const expectedVersion = cutoverAction === "create"
          ? 0
          : Number(field(form, "expectedVersion"))
        let commandType: FinanceConsoleCommandTypeV1 = "cutover.batch.create"
        let payload: FinanceConsoleCommandDraftV1["payload"]
        if (cutoverAction === "create") {
          payload = {
            cutoverVersion: 1,
            batchId,
            legalEntityId,
            branchId,
            importKind: field(form, "importKind"),
            sourceSystem: field(form, "sourceSystem"),
            sourceExportId: field(form, "sourceExportId"),
            sourceExportSha256: field(form, "sourceExportSha256"),
          }
        } else if (cutoverAction === "stage") {
          commandType = "cutover.record.stage"
          payload = {
            cutoverVersion: 1,
            batchId,
            sourceRecordType: field(form, "sourceRecordType"),
            sourceRecordId: field(form, "sourceRecordId"),
            sourceVersion: field(form, "sourceVersion"),
            sourceRecordSha256: field(form, "sourceRecordSha256"),
            payloadSha256: field(form, "payloadSha256"),
            payload: jsonRecord(form, "recordPayload"),
          }
        } else if (cutoverAction === "validate") {
          commandType = "cutover.batch.validate"
          payload = { cutoverVersion: 1, batchId }
        } else if (cutoverAction === "approve") {
          commandType = "cutover.batch.approve"
          payload = {
            cutoverVersion: 1,
            batchId,
            validationFingerprint: field(form, "validationFingerprint"),
            reason: field(form, "reason"),
          }
        } else if (cutoverAction === "master_commit") {
          commandType = "cutover.master_active.commit"
          payload = {
            cutoverVersion: 1,
            batchId,
            validationFingerprint: field(form, "validationFingerprint"),
          }
        } else if (cutoverAction === "finance_preview") {
          commandType = "cutover.open_finance.preview"
          payload = {
            cutoverVersion: 1,
            batchId,
            validationFingerprint: field(form, "validationFingerprint"),
            configurationId: field(form, "configurationId"),
            configurationVersion: Number(field(form, "configurationVersion")),
            currency: field(form, "currency"),
            postingDate: field(form, "postingDate"),
          }
        } else if (cutoverAction === "finance_commit") {
          commandType = "cutover.open_finance.commit"
          payload = {
            cutoverVersion: 1,
            batchId,
            validationFingerprint: field(form, "validationFingerprint"),
            previewId: field(form, "previewId"),
            previewFingerprint: field(form, "previewFingerprint"),
          }
        } else if (cutoverAction === "inventory_preview") {
          commandType = "cutover.inventory_opening.preview"
          payload = {
            cutoverVersion: 1,
            batchId,
            validationFingerprint: field(form, "validationFingerprint"),
            financePreviewId: field(form, "financePreviewId"),
            financePreviewFingerprint: field(form, "financePreviewFingerprint"),
            postingDate: field(form, "postingDate"),
          }
        } else {
          commandType = "cutover.inventory_opening.commit"
          payload = {
            cutoverVersion: 1,
            batchId,
            validationFingerprint: field(form, "validationFingerprint"),
            previewId: field(form, "previewId"),
            previewFingerprint: field(form, "previewFingerprint"),
          }
        }
        void submit(draft(
          commandType,
          "cutover_batch",
          batchId,
          legalEntityId,
          branchId,
          expectedVersion,
          payload,
        ))
      }
    } catch {
      setNotice("Complete every required field with a valid ID, date, amount, and reason.")
    }
  }

  const activeArea = areas.find(([id]) => id === area) ?? areas[0]
  const exceptionCount = report?.items.filter((item) =>
    item.status.includes("review") || item.status.includes("blocked"),
  ).length ?? 0

  return (
    <section className="finance-shell" aria-labelledby="finance-title">
      <header className="finance-hero">
        <div>
          <p className="eyebrow">Finance and accounting</p>
          <h2 id="finance-title">A clear close starts here.</h2>
          <p>
            Work from one Legal Entity at a time. Every balance, posting, and period
            decision stays anchored to Frappe and ERPNext.
          </p>
        </div>
        <div className="finance-scope-card">
          <span>Working books</span>
          <strong>{legalEntityName}</strong>
          <small>{branchName ?? "All authorized Branches"}</small>
        </div>
      </header>

      <div className="finance-layout">
        <nav className="finance-rail" aria-label="Finance workspaces">
          {areas.map(([id, label, detail]) => (
            <button
              type="button"
              key={id}
              className={area === id ? "active" : ""}
              onClick={() => selectArea(id)}
            >
              <span>{label}</span>
              <small>{detail}</small>
            </button>
          ))}
        </nav>

        <div className="finance-workspace">
          <div className="finance-toolbar">
            <div>
              <p className="eyebrow">{activeArea[1]}</p>
              <h3>{activeArea[2]}</h3>
            </div>
            <div className="finance-toolbar-actions">
              {area === "reports" && (
                <label>
                  <span className="sr-only">Report</span>
                  <select
                    value={reportKind}
                    onChange={(event) => setReportKind(
                      event.target.value as FinancialReportKindV1,
                    )}
                  >
                    {Object.entries(reportLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
              )}
              <button type="button" className="finance-quiet-button" onClick={() => {
                void Promise.all([loadReport(), loadReference()])
              }}>Refresh</button>
              {selectedAction && (
                <button
                  type="button"
                  className="finance-primary-button"
                  disabled={!canAct}
                  onClick={() => setActionOpen(true)}
                >
                  {selectedAction.label}
                </button>
              )}
            </div>
          </div>

          {notice && <div className="finance-notice" role="status">{notice}</div>}
          {selectedAction && referenceState === "loading" && (
            <div className="finance-notice" role="status">
              Loading the authoritative accounts and business references for this Branch.
            </div>
          )}
          {selectedAction && referenceState === "retry" && (
            <div className="finance-notice finance-notice-error" role="alert">
              <span>Finance references are unavailable. No action has been enabled.</span>
              <button type="button" className="finance-quiet-button" onClick={() => {
                void loadReference()
              }}>Retry references</button>
            </div>
          )}

          {area === "overview" && (
            <div className="finance-metrics" aria-label="Financial summary">
              <article><span>Net activity</span><strong>{formatMinor(report?.totals.netMinor ?? 0)}</strong><small>Current report window</small></article>
              <article><span>Debits</span><strong>{formatMinor(report?.totals.debitMinor ?? 0)}</strong><small>Authoritative entries</small></article>
              <article><span>Credits</span><strong>{formatMinor(report?.totals.creditMinor ?? 0)}</strong><small>Authoritative entries</small></article>
              <article className={exceptionCount > 0 ? "attention" : "clear"}><span>Needs attention</span><strong>{exceptionCount}</strong><small>Review or blocked items</small></article>
            </div>
          )}

          <div className="finance-report-card">
            <div className="finance-report-heading">
              <div>
                <span className="finance-source-dot" />
                <strong>{reportLabels[reportKind]}</strong>
                <small>Frappe / ERPNext</small>
              </div>
              {report && <time dateTime={report.observedAt}>Observed {new Date(report.observedAt).toLocaleString()}</time>}
            </div>

            {loadState === "loading" && <InteractionState tone="loading" title="Reading the books" detail="Loading the selected Legal Entity from Frappe." />}
            {loadState === "denied" && <InteractionState tone="denied" title="Finance access unavailable" detail="This Membership cannot read the selected scope. No other Tenant or Legal Entity was searched." />}
            {loadState === "retry" && <InteractionState tone="error" title="The current report is unavailable" detail="Nothing was changed. Retry when the authoritative service is available." action={<button type="button" className="finance-quiet-button" onClick={() => { void loadReport() }}>Retry report</button>} />}
            {loadState === "ready" && report?.items.length === 0 && <InteractionState tone="empty" title="No entries in this window" detail="The authoritative report returned no matching financial activity." />}
            {loadState === "ready" && report && report.items.length > 0 && (
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead><tr><th>Date</th><th>Reference</th><th>Status</th><th>Category</th><th className="number">Debit</th><th className="number">Credit</th></tr></thead>
                  <tbody>
                    {report.items.map((item) => (
                      <tr key={item.itemId}>
                        <td>{item.occurredOn}</td>
                        <td><strong>{item.reference}</strong><small>{item.itemId.slice(0, 8)}</small></td>
                        <td><span className={`finance-status ${item.status}`}>{statusLabel(item.status)}</span></td>
                        <td>{statusLabel(item.category)}</td>
                        <td className="number">{item.debitMinor ? formatMinor(item.debitMinor, item.currency) : "—"}</td>
                        <td className="number">{item.creditMinor ? formatMinor(item.creditMinor, item.currency) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={4}>Report total</td><td className="number">{formatMinor(report.totals.debitMinor)}</td><td className="number">{formatMinor(report.totals.creditMinor)}</td></tr></tfoot>
                </table>
              </div>
            )}
          </div>

          {receipt && (
            <div className={`finance-receipt ${receipt.ok ? "confirmed" : "failed"}`}>
              <strong>{receipt.ok ? "Command confirmed" : "Command not applied"}</strong>
              <span>{receipt.ok ? receipt.correlationId : receipt.error.code}</span>
            </div>
          )}
        </div>
      </div>

      {actionOpen && selectedAction && (
        <dialog
          ref={actionDialogRef}
          className="finance-dialog-backdrop"
          aria-labelledby="finance-action-title"
          onCancel={(event) => {
            event.preventDefault()
            setActionOpen(false)
          }}
        >
          <section className="finance-dialog">
            <header>
              <div><p className="eyebrow">{activeArea[1]}</p><h3 id="finance-action-title">{selectedAction.label}</h3></div>
              <button type="button" aria-label="Close" onClick={() => setActionOpen(false)}>×</button>
            </header>
            <form onSubmit={submitAction}>
              <FinanceActionFields area={area} report={report} receipt={receipt} reference={reference} legalEntityName={legalEntityName} branchName={branchName} />
              <div className="finance-command-note">
                <strong>Authoritative confirmation required</strong>
                <p>Frappe will recheck your Membership, Legal Entity, Branch, current version, period state, balance, and idempotency before commit.</p>
              </div>
              <footer>
                <button type="button" className="finance-quiet-button" onClick={() => setActionOpen(false)}>Cancel</button>
                <button type="submit" className="finance-primary-button" disabled={actionPending || !periodCanSubmit}>{actionPending ? "Awaiting Frappe…" : periodReopenPreview ? "Confirm sandbox MFA & reopen" : periodClosePreview ? "Close period" : selectedAction.label}</button>
              </footer>
            </form>
          </section>
        </dialog>
      )}
    </section>
  )
}

function FinanceActionFields({ area, report, receipt, reference, legalEntityName, branchName }: {
  area: FinanceArea
  report: FinancialReportV1 | null
  receipt: CommandResultV1 | null
  reference: FinanceReferenceDataV1 | null
  legalEntityName: string
  branchName: string | null
}) {
  const defaultReference = report?.items[0]?.itemId ?? ""
  const configuration = reference?.configuration
  const accountOptions = reference?.accounts ?? []
  if (area === "chart") return <>
    <div className="finance-form-grid">
      <label><span>Configuration ID</span><input name="configurationId" defaultValue={configuration?.configurationId ?? crypto.randomUUID()} readOnly /></label>
      <label><span>Current version</span><input name="expectedVersion" type="number" min="0" defaultValue={configuration?.version ?? 0} readOnly /></label>
      <label className="wide"><span>ERPNext Company</span><input name="company" defaultValue={reference?.company ?? legalEntityName} readOnly /></label>
      <label><span>Fiscal year</span><input name="fiscalYear" defaultValue={configuration?.fiscalYear ?? String(new Date().getUTCFullYear())} required /></label>
      <label><span>Currency</span><input name="currency" defaultValue={reference?.currency ?? "USD"} maxLength={3} readOnly /></label>
      <label><span>Fiscal year start</span><input name="fiscalYearStart" type="date" defaultValue={configuration?.fiscalYearStart ?? yearStart} required /></label>
      <label><span>Fiscal year end</span><input name="fiscalYearEnd" type="date" defaultValue={configuration?.fiscalYearEnd ?? yearEnd} required /></label>
      <AccountSelect name="receivableAccount" label="Receivable account" accounts={accountOptions} defaultValue={configuration?.accounts.receivableAccount} root="Asset" type="Receivable" />
      <AccountSelect name="payableAccount" label="Payable account" accounts={accountOptions} defaultValue={configuration?.accounts.payableAccount} root="Liability" type="Payable" />
      <AccountSelect name="incomeAccount" label="Income account" accounts={accountOptions} defaultValue={configuration?.accounts.incomeAccount} root="Income" />
      <AccountSelect name="expenseAccount" label="Expense account" accounts={accountOptions} defaultValue={configuration?.accounts.expenseAccount} root="Expense" />
      <AccountSelect name="roundOffAccount" label="Round-off account" accounts={accountOptions} defaultValue={configuration?.accounts.roundOffAccount} root="Expense" />
      <label><span>Cost center</span><input name="costCenter" defaultValue={reference?.costCenter ?? branchName ?? "Main"} readOnly /></label>
      <label className="wide"><span>Accounting dimension</span><input name="accountingDimension" defaultValue={reference?.accountingDimension ?? "Branch"} readOnly /></label>
    </div>
  </>
  if (area === "journals") return <JournalActionFields
    receipt={receipt}
    reference={reference}
    branchName={branchName}
  />
  if (area === "receivables" || area === "payables") {
    return <LedgerActionFields
      receivable={area === "receivables"}
      report={report}
      reference={reference}
      branchName={branchName}
    />
  }
  if (area === "tax") return <div className="finance-form-grid">
    <label className="wide"><span>Tax review</span><select name="reviewId" defaultValue={reference?.taxReviews[0]?.reviewId ?? defaultReference} required>{reference?.taxReviews.map((review) => <option key={review.reviewId} value={review.reviewId}>{review.reasonCode.replaceAll("_", " ")} · {review.reviewId.slice(0, 8)}</option>)}</select></label>
    <label><span>Expected version</span><input name="expectedVersion" type="number" min="1" defaultValue={reference?.taxReviews[0]?.version ?? 1} required /></label>
    <label><span>Decision</span><select name="decision" defaultValue="approve"><option value="approve">Approve selected rule</option><option value="reject">Reject treatment</option></select></label>
    <label className="wide"><span>Rule version</span><select name="ruleVersionId" required>{reference?.taxRuleVersions.map((rule) => <option key={rule.ruleVersionId} value={rule.ruleVersionId}>{rule.label}</option>)}</select></label>
    <label className="wide"><span>Review reason</span><textarea name="reason" minLength={12} required /></label>
  </div>
  if (area === "periods" && receipt?.ok && receipt.result.state === "preview") {
    const blockers = Array.isArray(receipt.result.blockers)
      ? receipt.result.blockers.length
      : 0
    const canClose = receipt.result.canClose === true
    return <div className="finance-form-grid">
      <input type="hidden" name="periodOperation" value="close" />
      <div className={`finance-form-explainer wide ${canClose ? "ready" : "blocked"}`}>
        <strong>{canClose ? "Preview is ready" : "Close is blocked"}</strong>
        <p>{canClose ? "The exact preview fingerprint will be bound to the close command." : `${blockers} blocker group${blockers === 1 ? "" : "s"} must be resolved before this period can close.`}</p>
      </div>
      <label className="wide"><span>Close reason</span><textarea name="reason" minLength={12} disabled={!canClose} required={canClose} /></label>
    </div>
  }
  if (area === "periods" && receipt?.ok && receipt.result.state === "reopen_preview") {
    return <div className="finance-form-grid">
      <input type="hidden" name="periodOperation" value="reopen" />
      <div className="finance-form-explainer wide ready">
        <strong>Reopen impact is ready</strong>
        <p>The final command will be bound to this exact fingerprint and a short-lived, command-specific sandbox MFA assertion.</p>
      </div>
      <label className="wide"><span>Reopen reason</span><textarea name="reason" minLength={12} required /></label>
      <label className="wide finance-check"><input type="checkbox" name="sandboxStepUpConfirmed" required /><span>I completed the sandbox MFA confirmation for this Accounting Period.</span></label>
    </div>
  }
  if (area === "periods") return <PeriodActionFields report={report} />
  if (area === "reconciliation") return <div className="finance-form-grid">
    <label><span>Created from</span><input name="createdFrom" type="date" defaultValue={new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10)} required /></label>
    <label><span>Created to</span><input name="createdTo" type="date" defaultValue={today} required /></label>
    <div className="finance-form-explainer wide"><strong>Provider-bounded read</strong><p>Reads at most 100 Stripe balance transactions over no more than 31 days for this Legal Entity’s connected account.</p></div>
  </div>
  return <CutoverActionFields receipt={receipt} reference={reference} />
}

function PeriodActionFields({ report }: { report: FinancialReportV1 | null }) {
  const [operation, setOperation] = useState("close")
  const closedPeriods = report?.items.filter((item) => item.status === "closed") ?? []
  return <div key={operation} className="finance-form-grid">
    <label><span>Operation</span><select name="periodOperation" value={operation} onChange={(event) => setOperation(event.target.value)}><option value="close">Preview and close</option><option value="reopen">Preview and reopen</option></select></label>
    {operation === "close" ? <>
      <label><span>New period ID</span><input name="periodId" defaultValue={crypto.randomUUID()} readOnly required /></label>
      <label><span>Start date</span><input name="startDate" type="date" defaultValue={yearStart} required /></label>
      <label><span>End date</span><input name="endDate" type="date" defaultValue={today} required /></label>
    </> : <label className="wide"><span>Closed Accounting Period</span><select name="periodId" required>{closedPeriods.map((item) => <option key={item.itemId} value={item.itemId}>{item.reference} · {item.occurredOn}</option>)}</select></label>}
    <div className="finance-form-explainer wide"><strong>Preview first</strong><p>{operation === "close" ? "A close creates a new immutable period record after Frappe confirms the exact date window has no blockers." : "A reopen preserves the closed record’s history and requires a command-bound step-up assertion."}</p></div>
  </div>
}

function CutoverActionFields({ receipt, reference }: {
  receipt: CommandResultV1 | null
  reference: FinanceReferenceDataV1 | null
}) {
  const [action, setAction] = useState("create")
  const result = receipt?.ok ? receipt.result : null
  const resultString = (name: string) => result && typeof result[name] === "string"
    ? result[name] as string
    : ""
  const resultNumber = (name: string, fallback = 1) => result && typeof result[name] === "number"
    ? result[name] as number
    : fallback
  const batchId = resultString("batchId") || resultString("entityId")
  const validationFingerprint = resultString("validationFingerprint")
  const previewId = resultString("entityId")
  const previewFingerprint = resultString("previewFingerprint")
  const needsBatch = action !== "create"
  const needsValidation = ["approve", "master_commit", "finance_preview", "finance_commit", "inventory_preview", "inventory_commit"].includes(action)
  return <div key={action} className="finance-form-grid">
    <label className="wide"><span>Controlled step</span><select name="cutoverAction" value={action} onChange={(event) => setAction(event.target.value)}><option value="create">Create staging batch</option><option value="stage">Stage source record</option><option value="validate">Validate batch</option><option value="approve">Approve batch</option><option value="master_commit">Commit master / active data</option><option value="finance_preview">Preview open finance</option><option value="finance_commit">Commit open finance</option><option value="inventory_preview">Preview inventory opening</option><option value="inventory_commit">Commit inventory opening</option></select></label>
    {needsBatch && <>
      <label><span>Batch ID</span><input name="batchId" defaultValue={batchId} required /></label>
      <label><span>Current version</span><input name="expectedVersion" type="number" min="1" defaultValue={resultNumber("version")} required /></label>
    </>}
    {action === "create" && <>
      <label><span>Import kind</span><select name="importKind" defaultValue="opening_balance"><option value="master_data">Master data</option><option value="active_work">Active work</option><option value="open_ar_ap">Open AR/AP</option><option value="opening_balance">Opening balances</option><option value="inventory_opening">Inventory opening</option></select></label>
      <label><span>Source system</span><input name="sourceSystem" required /></label>
      <label className="wide"><span>Source export ID</span><input name="sourceExportId" required /></label>
      <HashField name="sourceExportSha256" label="Source export SHA-256" />
    </>}
    {action === "stage" && <>
      <label><span>Source record type</span><input name="sourceRecordType" required /></label>
      <label><span>Source record ID</span><input name="sourceRecordId" required /></label>
      <label><span>Source version</span><input name="sourceVersion" required /></label>
      <HashField name="sourceRecordSha256" label="Source record SHA-256" />
      <HashField name="payloadSha256" label="Canonical payload SHA-256" />
      <label className="wide"><span>Product-owned record payload</span><textarea name="recordPayload" defaultValue="{}" required /></label>
    </>}
    {needsValidation && <HashField name="validationFingerprint" label="Validation fingerprint" defaultValue={validationFingerprint} />}
    {action === "approve" && <label className="wide"><span>Approval reason</span><textarea name="reason" minLength={12} required /></label>}
    {action === "finance_preview" && <>
      <label><span>Configuration ID</span><input name="configurationId" defaultValue={reference?.configuration?.configurationId ?? ""} readOnly required /></label>
      <label><span>Configuration version</span><input name="configurationVersion" type="number" min="1" defaultValue={reference?.configuration?.version ?? 1} readOnly required /></label>
      <label><span>Currency</span><input name="currency" defaultValue={reference?.currency ?? "USD"} readOnly /></label>
      <label><span>Posting date</span><input name="postingDate" type="date" defaultValue={today} required /></label>
    </>}
    {["finance_commit", "inventory_commit"].includes(action) && <>
      <label><span>Preview ID</span><input name="previewId" defaultValue={previewId} required /></label>
      <HashField name="previewFingerprint" label="Preview fingerprint" defaultValue={previewFingerprint} />
    </>}
    {action === "inventory_preview" && <>
      <label><span>Finance preview ID</span><input name="financePreviewId" defaultValue={previewId} required /></label>
      <HashField name="financePreviewFingerprint" label="Finance preview fingerprint" defaultValue={previewFingerprint} />
      <label><span>Posting date</span><input name="postingDate" type="date" defaultValue={today} required /></label>
    </>}
    <div className="finance-form-explainer wide"><strong>Explicit stages, no silent commit</strong><p>Source staging, validation, approval, financial preview, and adapter commit are independent idempotent commands. A successful earlier step never implies the next step ran.</p></div>
  </div>
}

function HashField({ name, label, defaultValue }: { name: string; label: string; defaultValue?: string | undefined }) {
  return <label className="wide"><span>{label}</span><input name={name} defaultValue={defaultValue} pattern="[a-f0-9]{64}" required /></label>
}

function JournalActionFields({ receipt, reference, branchName }: {
  receipt: CommandResultV1 | null
  reference: FinanceReferenceDataV1 | null
  branchName: string | null
}) {
  const [action, setAction] = useState("post")
  const configuration = reference?.configuration
  const accounts = reference?.accounts ?? []
  const lastPostingId = receipt?.ok && typeof receipt.result.entityId === "string"
    ? receipt.result.entityId
    : ""
  return <div key={action} className="finance-form-grid">
    <label><span>Action</span><select name="journalAction" value={action} onChange={(event) => setAction(event.target.value)}><option value="post">Post balanced journal</option><option value="reverse">Reverse posting</option><option value="late_adjustment">Post late adjustment</option><option value="correction">Link correction</option></select></label>
    {action === "reverse" ? <>
      <label><span>Posting ID</span><input name="postingId" defaultValue={lastPostingId} required /></label>
      <label><span>Current version</span><input name="expectedVersion" type="number" min="1" defaultValue="1" required /></label>
    </> : action === "correction" ? <FinancialCorrectionSourceFields reference={reference} /> : <>
      {action === "late_adjustment" && <LateEventSourceFields reference={reference} />}
      <label><span>Configuration ID</span><input name="configurationId" defaultValue={configuration?.configurationId ?? ""} readOnly required /></label>
      <label><span>Configuration version</span><input name="configurationVersion" type="number" min="1" defaultValue={configuration?.version ?? 1} readOnly required /></label>
      <label><span>Currency</span><input name="currency" defaultValue={reference?.currency ?? "USD"} maxLength={3} readOnly /></label>
      <AccountSelect name="debitAccount" label="Debit account" accounts={accounts} />
      <AccountSelect name="creditAccount" label="Credit account" accounts={accounts} />
      {action !== "late_adjustment" && <label><span>Amount</span><input name="amount" type="number" min="0.01" step="0.01" required /></label>}
      <label><span>Cost center</span><input name="costCenter" defaultValue={reference?.costCenter ?? branchName ?? "Main"} readOnly /></label>
    </>}
    <label><span>Posting date</span><input name="postingDate" type="date" defaultValue={today} required /></label>
    <label className="wide"><span>Reason</span><textarea name="reason" minLength={12} required /></label>
    {action === "late_adjustment" && <div className="finance-form-explainer wide"><strong>Current open period only</strong><p>The source event remains historically dated while this adjustment posts into the current open period with reconciliation linkage.</p></div>}
  </div>
}

function LateEventSourceFields({ reference }: {
  reference: FinanceReferenceDataV1 | null
}) {
  const events = reference?.lateEvents ?? []
  const [selectedId, setSelectedId] = useState(events[0]?.evidenceId ?? "")
  const selected = events.find((event) => event.evidenceId === selectedId)
  return <>
    <label className="wide"><span>Late-event evidence</span><select name="sourceId" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} required>{events.map((event) => <option key={event.evidenceId} value={event.evidenceId}>{event.sourceType} · {event.originalPostingDate} · {formatMinor(event.amountMinor, event.currency)}</option>)}</select></label>
    <label><span>Source version</span><input name="sourceVersion" type="number" value={selected?.sourceVersion ?? ""} readOnly required /></label>
    <label><span>Evidence amount</span><input name="amount" value={selected ? (selected.amountMinor / 100).toFixed(2) : ""} readOnly required /></label>
  </>
}

function FinancialCorrectionSourceFields({ reference }: {
  reference: FinanceReferenceDataV1 | null
}) {
  const postings = reference?.financialPostings ?? []
  const [selectedId, setSelectedId] = useState(postings[0]?.postingId ?? "")
  const selected = postings.find((posting) => posting.postingId === selectedId)
  return <>
    <label className="wide"><span>Authoritative posting</span><select name="sourceId" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} required>{postings.map((posting) => <option key={posting.postingId} value={posting.postingId}>{posting.journalEntry} · {posting.state} · {formatMinor(posting.totalMinor, posting.currency)}</option>)}</select></label>
    <label><span>Source version</span><input name="sourceVersion" type="number" value={selected?.version ?? ""} readOnly required /></label>
    <label><span>Amount</span><input value={selected ? formatMinor(selected.totalMinor, selected.currency) : ""} readOnly /></label>
    <input type="hidden" name="sourceCurrency" value={selected?.currency ?? ""} />
    <input type="hidden" name="sourceAmountMinor" value={selected?.totalMinor ?? ""} />
    <input type="hidden" name="originalEventAt" value={selected?.originalEventAt ?? ""} />
    <input type="hidden" name="journalEntry" value={selected?.journalEntry ?? ""} />
  </>
}

function LedgerActionFields({ receivable, report, reference, branchName }: {
  receivable: boolean
  report: FinancialReportV1 | null
  reference: FinanceReferenceDataV1 | null
  branchName: string | null
}) {
  const [action, setAction] = useState("invoice")
  const configuration = reference?.configuration
  const accounts = reference?.accounts ?? []
  const existing = action !== "invoice"
  return <div key={action} className="finance-form-grid">
    <label><span>Action</span><select name="ledgerAction" value={action} onChange={(event) => setAction(event.target.value)}><option value="invoice">Create invoice</option><option value="payment">Record payment</option><option value="credit">Issue credit</option><option value="write_off">Write off</option><option value="correct">Correct</option></select></label>
    {existing && <>
      <label><span>{receivable ? "Receivable" : "Payable"}</span><select name="recordId" required>{report?.items.map((item) => <option key={item.itemId} value={item.itemId}>{item.reference} · {formatMinor(receivable ? item.creditMinor : item.debitMinor, item.currency)}</option>)}</select></label>
      <label><span>Current version</span><input name="expectedVersion" type="number" min="1" defaultValue="1" required /></label>
    </>}
    {action === "invoice" && <>
      <label className="wide"><span>{receivable ? "Customer" : "Supplier"}</span><select name="party" required>{(receivable ? reference?.customers : reference?.suppliers)?.map((name) => <option key={name}>{name}</option>)}</select></label>
      <label><span>Item code</span><select name="itemCode" required>{reference?.items.map((name) => <option key={name}>{name}</option>)}</select></label>
      <label><span>Cost center</span><input name="costCenter" defaultValue={reference?.costCenter ?? branchName ?? "Main"} readOnly /></label>
      <AccountSelect name="primaryAccount" label={`${receivable ? "Income" : "Expense"} account`} accounts={accounts} defaultValue={receivable ? configuration?.accounts.incomeAccount : configuration?.accounts.expenseAccount} root={receivable ? "Income" : "Expense"} />
      <AccountSelect name="controlAccount" label={`${receivable ? "Receivable" : "Payable"} account`} accounts={accounts} defaultValue={receivable ? configuration?.accounts.receivableAccount : configuration?.accounts.payableAccount} root={receivable ? "Asset" : "Liability"} type={receivable ? "Receivable" : "Payable"} />
      <label><span>Due date</span><input name="dueDate" type="date" defaultValue={today} required /></label>
      <label className="wide"><span>Description</span><textarea name="description" required /></label>
    </>}
    {action === "payment" && <>
      <AccountSelect name="settlementAccount" label={receivable ? "Deposit account" : "Payment account"} accounts={accounts} root="Asset" />
      <label><span>Reference number</span><input name="referenceNumber" required /></label>
      <label><span>Reference date</span><input name="referenceDate" type="date" defaultValue={today} required /></label>
    </>}
    {action === "write_off" && <AccountSelect name="writeOffAccount" label="Write-off account" accounts={accounts} root="Expense" />}
    {["credit", "write_off", "correct"].includes(action) && <label className="wide"><span>Reason</span><textarea name="reason" minLength={12} required /></label>}
    <label><span>Posting date</span><input name="postingDate" type="date" defaultValue={today} required /></label>
    <label><span>Amount</span><input name="amount" type="number" min="0.01" step="0.01" required /></label>
    <label><span>Currency</span><input name="currency" defaultValue={reference?.currency ?? "USD"} maxLength={3} readOnly /></label>
  </div>
}

function AccountSelect({ name, label, accounts, defaultValue, root, type }: {
  name: string
  label: string
  accounts: FinanceReferenceDataV1["accounts"]
  defaultValue?: string | undefined
  root?: FinanceReferenceDataV1["accounts"][number]["rootType"]
  type?: string
}) {
  const options = accounts.filter((account) =>
    (root === undefined || account.rootType === root)
    && (type === undefined || account.accountType === type),
  )
  return <label>
    <span>{label}</span>
    <select name={name} defaultValue={defaultValue} required>
      {options.map((account) => <option key={account.name} value={account.name}>{account.name}</option>)}
    </select>
  </label>
}
